import ChartType from 'components/ChartType/ChartType';
import Preloader from 'components/Preloader';
import {
    CEP_PRODUCT_KEY,
    CEP_RISK_PROFILE,
    INVESTOR_PROFILE_CONTACT_ID, PIE, PRODUCT_EXTERNAL_ID, strategyTypes,
} from 'constants/constants';
import { useFormatting } from 'locale';
import { useClientProfile } from 'prodivers/clientProfile';
import React, {
    useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import { allocationPercentFormat } from 'utils/formatting';
import {
    FormGroup, Title, notification,
} from 'ui-library';
import { usePortfolio } from 'domain/Portfolio';
import { useNavigate, useParams } from 'react-router-dom';
import useLayoutController from 'components/Stepper/RouteBasedStepper/useLayoutController';
import { getSetupData } from 'additiv-services/setup';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useProductAllocation } from '../../../../hooks/useProductAllocation';
import ServiceManager from '../../../../../../../../services/ServiceManager';
import { riskCategoriesByLang } from '../../../../constants';
import RiskScale from '../../../../components/RiskScale/RiskScale';

import './ChangeOverview.css';
import { adaptModelType } from '../../../../adapters/adaptModelPortfolio';
import { adaptAllocations } from '../../../SinglePortfolioView/pages/Strategy/adapters/adaptProposalData';
import { getClientId } from '../../../../../../../../utils';
import { riskCategories } from '../../../../../../constants';

const setupData = getSetupData();

// --- Interfaces ---

interface ModelPortfolio {
    Id: string | number;
    Name?: string;
    Description?: string;
    CalculatedRiskScore?: number;
    Attributes?: any;
    Positions?: any[];
}

interface ChangeOverviewProps {
    datasource: () => {
        newProductExternalId?: string;
        strategy: {
            data: {
                newModelPortfolio: ModelPortfolio;
                strategyName: string;
                strategyId: string | number;
                investmentHorizon: any;
                individualPortfolioPositions: any;
                individualPortfolio: boolean;
                rebalancing: any;
                withdrawalOption: any;
                newWithdrawalData: any;
            };
            removeData: () => void;
        };
        strategies: any[];
        isThreeBProduct: boolean;
    };
}

// --- Utils ---

const adaptDescription = (description: string): ReactElement[] => description.split('\n').reduce((akku: ReactElement[], item: string, index: number) => {
    const trimItem = item.trim();

    if (trimItem.length > 0) {
        akku.push(
            <p className="paragraph" key={index}>
                {trimItem}
            </p>,
        );
    }

    return akku;
}, []);

// --- Component ---

const ChangeOverview: React.FC<ChangeOverviewProps> = ({ datasource }) => {
    const navigate = useNavigate();
    const { portfolioId, dfsClientId } = useParams<{ portfolioId: string; dfsClientId: string }>();
    const clientId = useMemo(() => getClientId(dfsClientId), [dfsClientId]);
    const { i18n: { language }, t } = useTranslation();

    const [isSending, setSending] = useState<boolean>(false);

    const { portfolio: { dataRaw: portfolio } } = usePortfolio();

    const {
        newProductExternalId,
        strategy: {
            data: {
                newModelPortfolio: changedModelPortfolio,
                strategyName,
                strategyId,
                investmentHorizon,
                individualPortfolioPositions: individualPortfolioDataForNewPlan,
                individualPortfolio,
                rebalancing,
                withdrawalOption,
                newWithdrawalData,
            },
            removeData,
        },
        strategies,
        isThreeBProduct: is3bProduct,
    } = datasource();

    // --- Old plan data: dependent React Query chain ---
    const { data: lastApplicationRes, error: lastApplicationError } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getLastApplicationByPortfolio',
        payload: { portfolioId, language },
        enabled: !!portfolioId,
    });

    const investmentApplicationId = lastApplicationRes?.data?.investmentApplicationId;

    const { data: investmentApplicationRes, error: investmentApplicationError } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getInvestmentApplication',
        payload: { investmentApplicationId },
        enabled: !!investmentApplicationId,
    });

    const investmentDescription = investmentApplicationRes?.data?.investmentDescription;
    const portfolioProposalId = investmentDescription?.portfolioProposalId;
    const mpId = investmentDescription?.selectStrategy?.modelPortfolioId;

    const { data: oldModelPortfolioRes, error: oldModelPortfolioError } = useGetCustomService({
        service: 'portfolioManagement',
        api: 'getModelPortfolio',
        payload: [mpId, { language }],
        enabled: !!mpId,
    });

    const newModelPortfolioId = changedModelPortfolio?.Id;

    const { data: newModelPortfolioRes, error: newModelPortfolioError } = useGetCustomService({
        service: 'portfolioManagement',
        api: 'getModelPortfolio',
        payload: [newModelPortfolioId, { language }],
        enabled: !!newModelPortfolioId,
    });

    const oldModelPortfolio = oldModelPortfolioRes?.data;
    const isIndividual = adaptModelType(oldModelPortfolio) === strategyTypes.INDIVIDUAL;

    const { data: portfolioProposalRes, error: portfolioProposalError } = useGetCustomService({
        service: 'portfolioManagement',
        api: 'getPortfolioProposal',
        payload: [clientId, portfolioProposalId, { language }],
        enabled: isIndividual && !!clientId && !!portfolioProposalId,
    });

    const newModelPortfolio = useMemo<ModelPortfolio>(() => (
        newModelPortfolioRes?.data
            ? { ...newModelPortfolioRes.data, CalculatedRiskScore: changedModelPortfolio?.CalculatedRiskScore }
            : changedModelPortfolio
    ), [newModelPortfolioRes, changedModelPortfolio]);

    const individualPortfolioDataForOldPlan = useMemo(() => (
        isIndividual && portfolioProposalRes?.data
            ? adaptAllocations(portfolioProposalRes.data.Positions)
            : { body: [], footer: {} }
    ), [isIndividual, portfolioProposalRes]);

    const oldPlanError = lastApplicationError
        || investmentApplicationError
        || oldModelPortfolioError
        || newModelPortfolioError
        || portfolioProposalError;

    // Loading until both model portfolios (and the proposal, when individual) resolve, or the chain errors.
    const isIndividualDataForOldPlanLoading = !(
        (!!oldModelPortfolioRes && !!newModelPortfolioRes && (!isIndividual || !!portfolioProposalRes))
        || !!oldPlanError
    );

    const oldProduct = useMemo(() => oldModelPortfolio, [oldModelPortfolio]);
    const newProduct = useMemo(() => newModelPortfolio, [newModelPortfolio]);
    const newProductId = useMemo(() => newModelPortfolio?.Id, [newModelPortfolio?.Id]);

    const selectedNewRisk = useMemo(() => {
        const modelPortfolioType = adaptModelType(newModelPortfolio);
        let strategyResult: any = strategyName;

        if (modelPortfolioType === strategyTypes.INDIVIDUAL) {
            const riskObj = Object.values(riskCategories).find((f: any) => f.Risk === newModelPortfolio?.CalculatedRiskScore);

            strategyResult = strategies?.find((f: any) => f.Id === (riskObj as any)?.Id);
        } else {
            strategyResult = strategies?.find((f: any) => f.Id === strategyId);
        }

        return strategyResult?.Name;
    }, [strategyName, newModelPortfolio, strategies]);

    const oldStrategyName = useMemo(() => portfolio?.RiskCategory?.Name, [portfolio?.RiskCategory?.Name]);
    const riskCategoriesList = useMemo(() => riskCategoriesByLang(t), [t]);

    const information = useClientProfile();
    const { getFormattedXAxisFormat } = useFormatting();

    const {
        allocations: newAllocations,
        isLoading: isNewAllocationsLoading,
        error: newProductAllocationError,
        riskScore: newPlanScore,
    } = useProductAllocation({
        productId: typeof newProductId === 'string' ? parseInt(newProductId, 10) : (newProductId as number),
        individualPortfolioData: individualPortfolioDataForNewPlan,
        doNotCalculateAllocations: true,
    });

    const {
        allocations: oldAllocations,
        isLoading: isOldAllocationsLoading,
        error: oldProductAllocationError,
        riskScore: oldPlanScore,
    } = useProductAllocation({
        productId: typeof oldProduct?.Id === 'string' ? parseInt(oldProduct.Id, 10) : (oldProduct?.Id as number),
        individualPortfolioData: individualPortfolioDataForOldPlan,
        doNotCalculateAllocations: true,
    });

    const onContinue = useCallback(async ({ withNewAPI = false }) => {
        try {
            setSending(true);

            let data:any;

            const payload = {
                strategyId,
                investmentHorizon,
                modelPortfolioId: newProductId,
                portfolioId,
                isNewFlow: true,
                individualPortfolio,
                ...(individualPortfolio ? { rebalancing } : {}),
                ...(newProductExternalId
                    ? { ProductExternalId: newProductExternalId }
                    : {}),
                ...(is3bProduct
                    ? {
                        withdrawalOption,
                        ...(withdrawalOption ? { ...newWithdrawalData } : {}),
                    }
                    : {}),
            };

            if (withNewAPI) {
                const { data: responseData } = await ServiceManager.customInvestmentService(
                    'changePlanApplicationNew',
                    [
                        { ...payload, contactId: clientId },
                    ],
                ) as any;

                data = responseData;
            } else {
                const { data: responseData } = await ServiceManager.customInvestmentService(
                    'changePlanApplication',
                    [
                        payload,
                    ],
                ) as any;

                data = responseData;
            }

            sessionStorage.removeItem(PRODUCT_EXTERNAL_ID);
            sessionStorage.removeItem(INVESTOR_PROFILE_CONTACT_ID);
            sessionStorage.removeItem(CEP_RISK_PROFILE);
            sessionStorage.removeItem(CEP_PRODUCT_KEY);
            removeData();

            if (data?.investmentDescription?.containerId) {
                navigate(
                    `/client/${dfsClientId}/container/${data?.investmentDescription?.containerId}`,
                );
            } else if (data.investmentApplicationId) {
                navigate(
                    `/client/${dfsClientId}/application/${data.investmentApplicationId}`,
                );
            } else {
                notification.open({
                    content: `${t('contactGroups.somethingWentWrong')}`,
                    type: 'error',
                });
            }
        } catch (err: any) {
            setPageErrors([err.toString()]);
        } finally {
            setSending(false);
        }
    }, [
        dfsClientId,
        portfolioId,
        investmentHorizon,
        strategyId,
        newProductId,
        individualPortfolio,
        rebalancing,
        removeData,
        withdrawalOption,
        newWithdrawalData,
        is3bProduct,
        newProductExternalId,
        navigate,
        t,
        clientId,
    ]);

    useEffect(() => {
        if (oldPlanError) {
            setPageErrors([oldPlanError?.message]);
        }
    }, [oldPlanError]);

    useEffect(() => {
        if (newProductAllocationError || oldProductAllocationError) {
            setPageErrors([newProductAllocationError || oldProductAllocationError]);
        }
    }, [newProductAllocationError, oldProductAllocationError]);

    const { setPageErrors } = useLayoutController({
        callbackOnContinue: useCallback(() => onContinue({ withNewAPI: setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG }), [onContinue]),
        isContinueDisabled: isSending || information?.isLoading || isOldAllocationsLoading || isIndividualDataForOldPlanLoading,
        isContinueLoading: isSending,
        isBackDisabled: false,
    });

    return (
        <div>
            <div className="change-overview">
                <div className="chart-wrapper" hidden={information?.isLoading}>
                    <Preloader
                        isLoading={information?.isLoading || isOldAllocationsLoading || isIndividualDataForOldPlanLoading}
                    >
                        <Title type={2}>{t('portfolio.changeStrategy.previousInvestmentStrategy')}</Title>
                        <Title type={3}>{oldProduct?.Name}</Title>
                        <Title type={3}>{oldStrategyName}</Title>
                        <div className="fi-strategy-layout fi-strategy-layout--pie-chart">
                            <div className="fi-pie-chart">
                                <ChartType
                                    data={oldAllocations}
                                    chartView={PIE}
                                    percentFormat={allocationPercentFormat}
                                    xAxisFormat={getFormattedXAxisFormat}
                                />
                            </div>
                            <div className="fi-product-description">
                                {oldProduct?.Description && adaptDescription(oldProduct.Description)}
                            </div>
                            <FormGroup>
                                <RiskScale scales={riskCategoriesList} activeIndex={oldPlanScore} />
                            </FormGroup>
                        </div>
                    </Preloader>
                </div>
                <div className="chart-wrapper" hidden={information?.isLoading}>
                    <Preloader
                        isLoading={information?.isLoading || isNewAllocationsLoading}
                    >
                        <Title type={2}>{t('portfolio.changeStrategy.newInvestmentStrategy')}</Title>
                        <Title type={3}>{newProduct?.Name}</Title>
                        <Title type={3}>{selectedNewRisk}</Title>
                        <div className="fi-strategy-layout fi-strategy-layout--pie-chart">
                            <div className="fi-pie-chart">
                                <ChartType
                                    data={newAllocations}
                                    chartView={PIE}
                                    percentFormat={allocationPercentFormat}
                                    xAxisFormat={getFormattedXAxisFormat}
                                />
                            </div>
                            <div className="fi-product-description">
                                {newProduct?.Description && adaptDescription(newProduct.Description)}
                            </div>
                            <FormGroup>
                                <RiskScale scales={riskCategoriesList} activeIndex={newPlanScore} />
                            </FormGroup>
                        </div>
                    </Preloader>
                </div>
            </div>
        </div>
    );
};

export default ChangeOverview;
